import React, { useState, useEffect } from 'react';
import { LogOut, CheckCircle, XCircle, Clock } from 'lucide-react';

function AdminDashboard({ onLogout }) {
  const [stories, setStories] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0 });
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selectedStory, setSelectedStory] = useState(null);

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    try {
      const response = await fetch('/api/admin/stories', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setStories(data);
      
      // Calculate stats from stories
      const total = data.length;
      const pending = data.filter(s => !s.approved).length;
      const approved = data.filter(s => s.approved).length;
      setStats({ total, pending, approved });
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching stories:', err);
      setLoading(false);
    }
  };

  const approveStory = async (id) => {
    try {
      await fetch(`/api/admin/stories/${id}/approve`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchStories();
    } catch (err) {
      alert('Error approving story');
    }
  };

  const deleteStory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this story?')) return;
    try {
      await fetch(`/api/admin/stories/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchStories();
    } catch (err) {
      alert('Error deleting story');
    }
  };

  const getFilteredStories = () => {
    if (filter === 'pending') return stories.filter(s => !s.approved);
    if (filter === 'approved') return stories.filter(s => s.approved);
    return stories;
  };

  if (loading) {
    return (
      <div className="bg-gray-800 text-white min-h-screen flex items-center justify-center">
        <p className="text-2xl">Loading...</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 text-white min-h-screen p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-12 border-b border-gray-700 pb-6">
          <h1 className="text-4xl font-bold">Admin Dashboard</h1>
          <button
            onClick={onLogout}
            className="flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-gray-700 p-6 rounded-lg border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Total Stories</p>
                <p className="text-4xl font-bold text-white">{stats.total}</p>
              </div>
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
          </div>

          <div className="bg-gray-700 p-6 rounded-lg border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Pending Review</p>
                <p className="text-4xl font-bold text-white">{stats.pending}</p>
              </div>
              <Clock className="w-12 h-12 text-yellow-500" />
            </div>
          </div>

          <div className="bg-gray-700 p-6 rounded-lg border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Approved</p>
                <p className="text-4xl font-bold text-white">{stats.approved}</p>
              </div>
              <CheckCircle className="w-12 h-12 text-blue-500" />
            </div>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setFilter('all')}
            className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
              filter === 'all'
                ? 'bg-white text-gray-800'
                : 'bg-gray-700 text-white hover:bg-gray-600'
            }`}
          >
            All Stories
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
              filter === 'pending'
                ? 'bg-yellow-500 text-white'
                : 'bg-gray-700 text-white hover:bg-gray-600'
            }`}
          >
            Pending
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
              filter === 'approved'
                ? 'bg-green-500 text-white'
                : 'bg-gray-700 text-white hover:bg-gray-600'
            }`}
          >
            Approved
          </button>
        </div>

        {/* Stories Table */}
        <div className="bg-gray-700 rounded-lg overflow-hidden shadow-2xl">
          {getFilteredStories().length === 0 ? (
            <div className="p-8 text-center text-gray-400">
              <p className="text-lg">No stories found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-600 border-b border-gray-500">
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold">Name</th>
                    <th className="px-6 py-4 text-left font-semibold">Email</th>
                    <th className="px-6 py-4 text-left font-semibold">Status</th>
                    <th className="px-6 py-4 text-left font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredStories().map((story) => (
                    <tr key={story.id} className="border-b border-gray-600 hover:bg-gray-600 transition-colors">
                      <td className="px-6 py-4">{story.name || 'Anonymous'}</td>
                      <td className="px-6 py-4">{story.email}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-sm font-semibold ${
                            story.approved
                              ? 'bg-green-500 text-white'
                              : 'bg-yellow-500 text-white'
                          }`}
                        >
                          {story.approved ? 'Approved' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-3">
                          <button
                            onClick={() => setSelectedStory(story)}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm font-semibold transition-colors"
                          >
                            View
                          </button>
                          {!story.approved && (
                            <button
                              onClick={() => approveStory(story.id)}
                              className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-sm font-semibold transition-colors flex items-center gap-1"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Approve
                            </button>
                          )}
                          <button
                            onClick={() => deleteStory(story.id)}
                            className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-sm font-semibold transition-colors flex items-center gap-1"
                          >
                            <XCircle className="w-4 h-4" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal for viewing story details */}
      {selectedStory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-700 rounded-lg p-8 max-w-2xl w-full max-h-96 overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">Story Details</h2>
            
            <div className="mb-4">
              <p className="text-gray-400 text-sm">Name</p>
              <p className="text-white text-lg">{selectedStory.name || 'Anonymous'}</p>
            </div>

            <div className="mb-4">
              <p className="text-gray-400 text-sm">Email</p>
              <p className="text-white text-lg">{selectedStory.email}</p>
            </div>

            <div className="mb-4">
              <p className="text-gray-400 text-sm">I used to believe</p>
              <p className="text-white text-lg bg-gray-600 p-3 rounded">
                {selectedStory.used_to_think || selectedStory.usedToThink || 'N/A'}
              </p>
            </div>

            <div className="mb-6">
              <p className="text-gray-400 text-sm">Now I understand</p>
              <p className="text-white text-lg bg-gray-600 p-3 rounded">
                {selectedStory.now_know || selectedStory.nowKnow || 'N/A'}
              </p>
            </div>

            <button
              onClick={() => setSelectedStory(null)}
              className="w-full px-6 py-3 bg-gray-600 hover:bg-gray-500 rounded-lg font-semibold transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
